import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, PlusCircle } from 'lucide-react';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { venueAPI, incidentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const INCIDENT_TYPES = [
  'customer_accident',
  'staff_injury',
  'fire_evacuation',
  'medical_emergency',
  'altercation',
  'property_damage',
  'theft',
  'other',
];

const toLabel = (value) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function IncidentReportsPage() {
  const { user } = useAuthStore();
  const canCreate = hasMinimumRole(user, ROLES.STAFF);

  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    incident_type: 'other',
    occurred_at: new Date().toISOString().slice(0, 16),
    location: '',
    description: '',
    action_taken: '',
    witness_details: '',
    cctv_captured: false,
    emergency_services: false,
    evidence_urls: '',
  });

  const visibleVenues = useMemo(() => {
    if (hasMinimumRole(user, ROLES.OPS)) return venues;
    const allowed = new Set(user?.venue_ids || []);
    return venues.filter((venue) => allowed.has(venue.id));
  }, [venues, user]);

  const loadIncidents = async (venueId) => {
    if (!venueId) {
      setIncidents([]);
      return;
    }
    const res = await incidentAPI.list(venueId, 50);
    setIncidents(res?.data?.incidents || []);
  };

  const initialize = async () => {
    setLoading(true);
    try {
      const venuesRes = await venueAPI.list();
      const venueList = venuesRes?.data || [];
      setVenues(venueList);

      const allowed = hasMinimumRole(user, ROLES.OPS)
        ? venueList
        : venueList.filter((venue) => (user?.venue_ids || []).includes(venue.id));

      const firstVenueId = allowed[0]?.id || '';
      setSelectedVenueId(firstVenueId);
      await loadIncidents(firstVenueId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleVenueChange = async (event) => {
    const nextVenueId = event.target.value;
    setSelectedVenueId(nextVenueId);
    setLoading(true);
    try {
      await loadIncidents(nextVenueId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedVenueId) {
      toast.error('Select a venue first');
      return;
    }

    setSaving(true);
    try {
      const evidenceUrls = form.evidence_urls
        .split(',')
        .map((url) => url.trim())
        .filter(Boolean);

      await incidentAPI.create({
        venue_id: selectedVenueId,
        incident_type: form.incident_type,
        occurred_at: new Date(form.occurred_at).toISOString(),
        location: form.location,
        description: form.description,
        action_taken: form.action_taken,
        witness_details: form.witness_details || null,
        cctv_captured: form.cctv_captured,
        emergency_services: form.emergency_services,
        evidence_urls: evidenceUrls,
      });

      toast.success('Incident report submitted');
      setForm((prev) => ({
        ...prev,
        incident_type: 'other',
        location: '',
        description: '',
        action_taken: '',
        witness_details: '',
        cctv_captured: false,
        emergency_services: false,
        evidence_urls: '',
      }));
      await loadIncidents(selectedVenueId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
          Incident Reports
        </h1>
        <p className="text-[#A1A1AA] mt-1">Log incidents and view recent reports by venue.</p>
      </div>

      <div className="card-rtb">
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Venue</Label>
        <select
          value={selectedVenueId}
          onChange={handleVenueChange}
          className="w-full mt-2 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
        >
          {visibleVenues.map((venue) => (
            <option key={venue.id} value={venue.id}>{venue.name}</option>
          ))}
        </select>
      </div>

      {canCreate && (
        <form onSubmit={handleCreate} className="card-rtb space-y-4">
          <div className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-[#FF6600]" />
            <h2 className="font-heading text-lg font-bold text-white uppercase">New Incident</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Type *</Label>
              <select
                value={form.incident_type}
                onChange={(e) => setForm((f) => ({ ...f, incident_type: e.target.value }))}
                className="w-full mt-1 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
                required
              >
                {INCIDENT_TYPES.map((type) => (
                  <option key={type} value={type}>{toLabel(type)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Occurred At *</Label>
              <Input
                type="datetime-local"
                className="input-rtb mt-1"
                value={form.occurred_at}
                onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Location *</Label>
              <Input
                className="input-rtb mt-1"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Evidence URLs (comma separated)</Label>
              <Input
                className="input-rtb mt-1"
                value={form.evidence_urls}
                onChange={(e) => setForm((f) => ({ ...f, evidence_urls: e.target.value }))}
                placeholder="https://... , https://..."
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Description *</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full mt-1 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
              rows={4}
              required
            />
          </div>

          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Action Taken *</Label>
            <textarea
              value={form.action_taken}
              onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))}
              className="w-full mt-1 bg-[#111111] border border-[#3F3F46] rounded-sm px-3 py-2 text-sm text-white"
              rows={3}
              required
            />
          </div>

          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Witness Details</Label>
            <Input
              className="input-rtb mt-1"
              value={form.witness_details}
              onChange={(e) => setForm((f) => ({ ...f, witness_details: e.target.value }))}
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-[#D4D4D8]">
              <input
                type="checkbox"
                checked={form.cctv_captured}
                onChange={(e) => setForm((f) => ({ ...f, cctv_captured: e.target.checked }))}
              />
              CCTV captured
            </label>
            <label className="flex items-center gap-2 text-sm text-[#D4D4D8]">
              <input
                type="checkbox"
                checked={form.emergency_services}
                onChange={(e) => setForm((f) => ({ ...f, emergency_services: e.target.checked }))}
              />
              Emergency services called
            </label>
          </div>

          <Button type="submit" className="btn-rtb-primary" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit Incident
          </Button>
        </form>
      )}

      <div className="card-rtb">
        <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Recent Incidents</h2>
        {incidents.length === 0 ? (
          <div className="text-center py-10 text-[#A1A1AA]">
            <AlertTriangle className="w-10 h-10 mx-auto text-[#3F3F46] mb-3" />
            No incidents logged for this venue.
          </div>
        ) : (
          <div className="space-y-3">
            {incidents.map((incident) => (
              <div key={incident.id} className="bg-[#1E1E1E] border border-[#3F3F46] rounded-sm p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="text-white font-semibold text-sm">{toLabel(incident.incident_type)}</p>
                  <p className="text-[#A1A1AA] text-xs">
                    {incident.occurred_at ? new Date(incident.occurred_at).toLocaleString('en-GB') : '—'}
                  </p>
                </div>
                <p className="text-[#D4D4D8] text-sm"><span className="text-[#A1A1AA]">Location:</span> {incident.location || '—'}</p>
                <p className="text-[#D4D4D8] text-sm mt-1 whitespace-pre-line">{incident.description || '—'}</p>
                {incident.action_taken && (
                  <p className="text-[#D4D4D8] text-sm mt-2">
                    <span className="text-[#A1A1AA]">Action:</span> {incident.action_taken}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
